import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, StickyNote } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import type { WaConversation, WaMessage } from "@/types/whatsapp";

export interface WhatsAppThread {
  conversation: WaConversation;
  messages: WaMessage[];
}

const fmt = (v: string) =>
  new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

/** A WhatsApp chat shown as one entry in a deal's activity timeline */
export function WhatsAppTimelineItem({ thread, onOpenChat }: { thread: WhatsAppThread; onOpenChat: () => void }) {
  const [open, setOpen] = useState(false);
  const c = thread.conversation;
  const recent = thread.messages.filter((m) => m.message_type !== "reaction" && !m.payload?.system).slice(-8);

  return (
    <div className="relative group">
      <div className="absolute -left-[21px] top-2 size-2.5 rounded-full ring-4 ring-card bg-[#25D366]" />
      <div className="bg-background border border-border/60 rounded-xl hover:border-border transition-colors shadow-2xs">
        <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left p-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Badge variant="outline" className="text-[10px] font-semibold tracking-wide bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366] border-[#25D366]/30 shrink-0 gap-1">
                <WhatsAppIcon className="size-3" />
                WHATSAPP
              </Badge>
              <span className={`text-xs truncate ${c.unread_count ? "font-bold" : "font-semibold"}`}>
                Chat with {c.profile_name || c.phone}
              </span>
              {c.unread_count > 0 && (
                <span className="text-[10px] px-1.5 rounded-full bg-emerald-500 text-white shrink-0">{c.unread_count} new</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {c.last_message_at && <span className="text-[11px] text-muted-foreground font-mono">{fmt(c.last_message_at)}</span>}
              <ChevronDown className={`size-3.5 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
            </div>
          </div>
          {!open && <p className="text-xs text-foreground/80 truncate">{c.last_message_preview}</p>}
        </button>
        {open && (
          <div className="px-3 pb-3 space-y-1.5">
            {recent.map((m) => (
              <div key={m.id} className={`flex ${m.direction === "IN" ? "justify-start" : "justify-end"}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs border ${
                    m.direction === "IN"
                      ? "bg-muted/40"
                      : m.direction === "NOTE"
                        ? "bg-amber-50 border-amber-200 dark:bg-amber-500/10"
                        : "bg-[#d9fdd3] border-[#c3eebc] dark:bg-[#005c4b] dark:border-[#005c4b]"
                  }`}
                >
                  {m.direction === "NOTE" && <StickyNote className="size-3 inline mr-1 text-amber-600" />}
                  <span className="whitespace-pre-wrap break-words">{m.body || m.caption || m.file_name || m.message_type}</span>
                  <div className="text-[10px] text-muted-foreground text-right mt-0.5">{fmt(m.message_at)}</div>
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-1">
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={onOpenChat}>
                <WhatsAppIcon className="size-3.5" />
                Open chat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
